import React from "react";

const PrivacyPolicy = () => {
  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "auto" }}>
      <h1>Kalingo Inc. Privacy Policy</h1>

      <p style={{ paddingLeft: "10px" }}>Effective Date: 10-fab-2025</p>

      <p style={{ paddingLeft: "10px" }}>
        Welcome to Kalingo Inc. (the "App," "we," "us," or "our"), an
        over-the-top (OTT) streaming platform dedicated to providing you with a
        rich library of movies, web series, and live TV content. We are
        committed to protecting your privacy and ensuring a safe and enjoyable
        experience on our platform. This Privacy Policy explains how we collect,
        use, and share your information when you use our App, accessible at
        kalingo.tv, and related services. By accessing or using KalingoTV, you
        agree to the terms of this Privacy Policy. If you do not agree with the
        terms of this Privacy Policy, please do not use KalingoTV.
      </p>

      <h2>Information We Collect</h2>

      <p style={{ paddingLeft: "10px" }}>
        We collect various types of information when you interact with
        KalingoTV, both directly and indirectly. This information helps us
        personalize your experience, improve our services, and ensure the
        security of our platform. The types of information we collect include:
      </p>

      <ul>
        <li>
          <b>Personal Information:</b> When you create an account on KalingoTV,
          we collect your phone number. This is essential for account creation,
          authentication via One-Time Password (OTP), and secure access to your
          account. We may also collect other personal information if you choose
          to provide it, such as your name, email address, age, gender, or
          preferred content genres. Providing this additional information is
          optional but can enhance your personalized experience on the platform.
        </li>
        <li>
          <b>Usage Information:</b> We track your activity within the App to
          understand how you use KalingoTV. This includes the content you
          browse, the movies, web series, and live TV channels you watch, the
          features you interact with (e.g., search, recommendations,
          watchlists), the time you spend on the App, and your interactions with
          other users (if applicable). This data helps us optimize content
          recommendations, improve the user interface, and personalize your
          viewing experience.
        </li>
        <li>
          <b>Device Information:</b> We collect information about the devices
          you use to access KalingoTV. This includes the device model, operating
          system (e.g., Android, iOS), unique device identifiers (e.g., IMEI,
          device ID), network information (e.g., IP address, Wi-Fi network),
          device settings (e.g., language, time zone), and mobile carrier. This
          information helps us ensure compatibility with different devices and
          optimize performance.
        </li>
        <li>
          <b>Log Information:</b> Our servers automatically record information
          about your interactions with KalingoTV ("log data"). This includes
          your IP address, access times, browser type, referring website, the
          pages you visit within the App, and other diagnostic data. Log data is
          essential for troubleshooting, security analysis, and improving the
          reliability of our services.
        </li>
        <li>
          <b>Location Information (With Permission):</b> We may request
          permission to collect your device's location. If you grant permission,
          we may use this information to provide location-based services, such
          as recommending content available in your region or personalizing
          advertisements. You can manage your location permissions through your
          device settings.
        </li>
        <li>
          <b>Payment Information (If Applicable):</b> If you subscribe to a
          premium plan or make in-app purchases, we will collect payment
          information, such as your credit card details or other payment method
          information. We use this information to process your payments and
          manage your subscriptions. We use secure payment gateways to protect
          your financial information.
        </li>
      </ul>

      <h2>How We Use Your Information</h2>

      <p style={{ paddingLeft: "10px" }}>
        We use the information we collect for a variety of purposes, all aimed
        at providing you with the best possible experience on KalingoTV:
      </p>

      <ul>
        <li>
          <b>Providing and Improving the Service:</b> We use your information to
          deliver the content you request, personalize your viewing experience,
          recommend relevant content, and improve the functionality and
          performance of KalingoTV. For example, we use your viewing history to
          suggest movies and shows you might enjoy.
        </li>
        <li>
          <b>Account Management:</b> Your phone number is crucial for creating
          and managing your KalingoTV account. We use it to verify your
          identity, send you OTPs for login, allow you to reset your password,
          and provide important notifications related to your account or our
          services.
        </li>
        <li>
          <b>Personalization:</b> We strive to personalize your experience on
          KalingoTV. By analyzing your usage data, we can recommend content that
          aligns with your interests, customize the App interface, and provide a
          more tailored viewing experience.
        </li>
        <li>
          <b>Communication:</b> We may use your information to contact you with
          updates about KalingoTV, new content releases, promotional offers
          (with your consent, where legally required), important service
          announcements, or to respond to your inquiries. You have the right to
          opt out of marketing communications at any time by adjusting your
          notification settings within the App or by contacting us directly.
        </li>
        <li>
          <b>Analytics:</b> We analyze aggregated and anonymized data to
          understand user trends, measure the effectiveness of our content and
          features, and identify areas for improvement. This data helps us make
          informed decisions about content acquisition, App development, and
          marketing strategies.
        </li>
        <li>
          <b>Legal Compliance:</b> We may use your information to comply with
          applicable laws, regulations, legal processes, or governmental
          requests. This includes responding to subpoenas, court orders, or
          other legal demands.
        </li>
        <li>
          <b>Security:</b> We use your information to protect the security and
          integrity of KalingoTV. This includes detecting and preventing fraud,
          unauthorized access, and other malicious activities.
        </li>
      </ul>

      <h2>Information Sharing</h2>

      <p style={{ paddingLeft: "10px" }}>
        We are committed to protecting your privacy and do not share your
        personal information with third parties except as described in this
        Privacy Policy. We may share your information with the following
        categories of recipients:
      </p>

      <ul>
        <li>
          <b>Service Providers:</b> We engage trusted third-party service
          providers to assist us in providing and improving KalingoTV. These
          providers may include hosting providers, cloud storage providers,
          content delivery networks (CDNs), analytics providers, marketing
          agencies, customer support providers, and payment processors. We have
          contracts with these providers that require them to protect your
          information and only use it for the purposes we specify.
        </li>
        <li>
          <b>Business Partners (With Consent):</b> We may share your information
          with business partners, such as content creators or advertisers, but
          only with your explicit consent. We will always be transparent about
          who we are sharing your information with and why.
        </li>
        <li>
          <b>Legal Authorities:</b> We may disclose your information to legal
          authorities if required by law, court order, or government request. We
          may also disclose your information to protect our legal rights or to
          investigate suspected illegal activity.
        </li>
        <li>
          <b>Affiliates:</b> We may share your information with our affiliated
          companies for the purposes described in this Privacy Policy.
        </li>
        <li>
          <b>Business Transfers:</b> In the event of a merger, acquisition, or
          sale of all or a portion of our assets, your information may be
          transferred as part of that transaction. We will notify you of any
          such transfer and provide you with the opportunity to opt out.
        </li>
      </ul>

      <h2>Data Security</h2>

      <p style={{ paddingLeft: "10px" }}>
        We take the security of your information very seriously and implement a
        variety of technical and organizational measures to protect it from
        unauthorized access, use, or disclosure. These measures include
        encryption, firewalls, access controls, and regular security audits.
        However, no method of transmission over the Internet or electronic
        storage is 100% secure. While we strive to protect your information, we
        cannot guarantee its absolute security. We encourage you to use a b
        password for your KalingoTV account and to keep it confidential.
      </p>

      <h2>Children's Privacy</h2>

      <p style={{ paddingLeft: "10px" }}>
        KalingoTV is not intended for children under the age of 13. We do not
        knowingly collect personal information from children under 13. If you
        become aware that a child under 13 has provided us with personal
        information, please contact us immediately. We will take steps to delete
        the information and terminate the child's account.
      </p>

      <h2>Changes to this Privacy Policy</h2>

      <p style={{ paddingLeft: "10px" }}>
        We may update this Privacy Policy from time to time to reflect changes
        in our practices or legal requirements. We will notify you of any
        material changes by posting the updated Privacy Policy on the App and/or
        by other means, such as email. Your continued use of KalingoTV after the
        effective date of the updated Privacy Policy constitutes your acceptance
        of the changes. We encourage you to review this Privacy Policy
        periodically for any updates.
      </p>

      <h2>Contact Us</h2>

      <p style={{ paddingLeft: "10px" }}>
        If you have any questions or concerns about this Privacy Policy, please
        contact us at:
      </p>

      <p style={{ paddingLeft: "10px" }}>
        Email: <a href="mailto:contact@kalingo.tv">contact@kalingo.tv</a>
      </p>

      <p style={{ paddingLeft: "10px" }}>Thank you for using KalingoTV!</p>
    </div>
  );
};

export default PrivacyPolicy;
